import React from 'react'
import {useIntl} from 'react-intl'
import clsx from 'clsx'
import DOMPurify from 'dompurify'
import {KTIcon} from '../../../../helpers'
import {useAuth} from '../../../../../app/modules/auth'

const ID_TYPE_LABELS: Record<string, string> = {
  PASSPORT: 'Passport',
  NATIONAL_ID: 'National ID',
  DRIVERS_LICENSE: "Driver's License",
  RESIDENCE_PERMIT: 'Residence Permit',
}

const SMS_TYPE_LABELS: Record<string, string> = {
  PROMOTIONAL: 'Promotional',
  TRANSACTIONAL: 'Transactional',
}

const FREQUENCY_LABELS: Record<string, string> = {
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  YEARLY: 'Yearly',
}

type Props = {
  kycRecord: any
}

const DrawerKYCDetails: React.FC<Props> = ({kycRecord}) => {
  const intl = useIntl()
  const {auth} = useAuth()

  return (
    <div
      id='kyc-details'
      className='bg-body'
      data-kt-drawer='true'
      data-kt-drawer-name='kyc-details'
      data-kt-drawer-activate='true'
      data-kt-drawer-overlay='true'
      data-kt-drawer-width="{default:'300px', 'lg': '900px'}"
      data-kt-drawer-direction='end'
      data-kt-drawer-toggle='#kyc-details-toggle'
      data-kt-drawer-close='#kyc-details-close'
    >
      <div className='card shadow-none rounded-0 w-100 h-100 d-flex flex-column'>
        <div className='card-header' id='kyc-details-header'>
          <h3 className='card-title fw-bolder text-dark d-flex align-items-center'>
            {intl.formatMessage({id: 'KYC.DETAILS_TITLE', defaultMessage: 'KYC Details'})}
            {kycRecord && (
              <span
                className={clsx('badge ms-3 fs-7', {
                  'badge-light-success': kycRecord.status === 'approved',
                  'badge-light-danger': kycRecord.status === 'rejected',
                  'badge-light-warning': kycRecord.status === 'pending',
                })}
              >
                {kycRecord.status === 'approved'
                  ? intl.formatMessage({id: 'COMMON.APPROVED', defaultMessage: 'Approved'})
                  : kycRecord.status === 'rejected'
                  ? intl.formatMessage({id: 'COMMON.REJECTED', defaultMessage: 'Rejected'})
                  : intl.formatMessage({id: 'COMMON.PENDING', defaultMessage: 'Pending'})}
              </span>
            )}
          </h3>
          <div className='card-toolbar'>
            <button
              type='button'
              className='btn btn-sm btn-icon btn-active-light-primary me-n5'
              id='kyc-details-close'
            >
              <KTIcon iconName='cross' className='fs-1' />
            </button>
          </div>
        </div>

        <div
          className='card-body position-relative'
          id='kyc-details-body'
          style={{flex: '1 1 0', minHeight: 0, overflow: 'hidden'}}
        >
          <div id='kyc-details-scroll' className='scroll-y h-100 me-n5 pe-5'>
            {kycRecord ? (
              <>
                {/* Business Information */}
                <div className='mb-8'>
                  <h5 className='fw-bold text-gray-700 mb-4 border-bottom pb-2'>
                    {intl.formatMessage({id: 'KYC.BUSINESS_INFO', defaultMessage: 'Business Information'})}
                  </h5>
                  <div className='row g-4'>
                    <div className='col-md-6'>
                      <div className='text-gray-500 fs-7 mb-1'>
                        {intl.formatMessage({id: 'KYC.BUSINESS_CATEGORY', defaultMessage: 'Business Category'})}
                      </div>
                      <div className='fw-bold text-gray-800'>{kycRecord.category}</div>
                    </div>
                    <div className='col-md-6'>
                      <div className='text-gray-500 fs-7 mb-1'>
                        {intl.formatMessage({id: 'KYC.LEGAL_ENTITY_NAME', defaultMessage: 'Legal Entity Name'})}
                      </div>
                      <div className='fw-bold text-gray-800'>{kycRecord.legalEntityName}</div>
                    </div>
                    <div className='col-md-6'>
                      <div className='text-gray-500 fs-7 mb-1'>
                        {intl.formatMessage({id: 'KYC.BRAND_NAME', defaultMessage: 'Brand Name'})}
                      </div>
                      <div className='fw-bold text-gray-800'>{kycRecord.brandName}</div>
                    </div>
                    <div className='col-md-6'>
                      <div className='text-gray-500 fs-7 mb-1'>
                        {intl.formatMessage({id: 'KYC.REGISTRATION_NUMBER', defaultMessage: 'Registration Number'})}
                      </div>
                      <div className='fw-bold text-gray-800'>{kycRecord.registrationNumber}</div>
                    </div>
                  </div>
                </div>

                {/* Registered Address */}
                <div className='mb-8'>
                  <h5 className='fw-bold text-gray-700 mb-4 border-bottom pb-2'>
                    {intl.formatMessage({id: 'KYC.REGISTERED_ADDRESS', defaultMessage: 'Registered Address'})}
                  </h5>
                  <div className='row g-4'>
                    <div className='col-md-12'>
                      <div className='text-gray-500 fs-7 mb-1'>
                        {intl.formatMessage({id: 'KYC.ADDRESS', defaultMessage: 'Address'})}
                      </div>
                      <div className='fw-bold text-gray-800'>{kycRecord.address}</div>
                    </div>
                    <div className='col-md-4'>
                      <div className='text-gray-500 fs-7 mb-1'>
                        {intl.formatMessage({id: 'KYC.CITY', defaultMessage: 'City'})}
                      </div>
                      <div className='fw-bold text-gray-800'>{kycRecord.city}</div>
                    </div>
                    <div className='col-md-4'>
                      <div className='text-gray-500 fs-7 mb-1'>
                        {intl.formatMessage({id: 'KYC.COUNTRY', defaultMessage: 'Country'})}
                      </div>
                      <div className='fw-bold text-gray-800'>{kycRecord.country}</div>
                    </div>
                    <div className='col-md-4'>
                      <div className='text-gray-500 fs-7 mb-1'>
                        {intl.formatMessage({id: 'KYC.POSTAL_CODE', defaultMessage: 'Postal Code'})}
                      </div>
                      <div className='fw-bold text-gray-800'>{kycRecord.postalCode}</div>
                    </div>
                  </div>
                </div>

                {/* Contact Details */}
                <div className='mb-8'>
                  <h5 className='fw-bold text-gray-700 mb-4 border-bottom pb-2'>
                    {intl.formatMessage({id: 'KYC.CONTACT_DETAILS', defaultMessage: 'Contact Details'})}
                  </h5>
                  <div className='row g-4'>
                    <div className='col-md-4'>
                      <div className='text-gray-500 fs-7 mb-1'>
                        {intl.formatMessage({id: 'KYC.AUTHORISED_PERSON', defaultMessage: 'Authorised Person'})}
                      </div>
                      <div className='fw-bold text-gray-800'>{kycRecord.authorisedPersonName}</div>
                    </div>
                    <div className='col-md-4'>
                      <div className='text-gray-500 fs-7 mb-1'>
                        {intl.formatMessage({id: 'KYC.DEPARTMENT', defaultMessage: 'Department'})}
                      </div>
                      <div className='fw-bold text-gray-800'>{kycRecord.department || '----'}</div>
                    </div>
                    <div className='col-md-4'>
                      <div className='text-gray-500 fs-7 mb-1'>
                        {intl.formatMessage({id: 'KYC.PHONE', defaultMessage: 'Phone'})}
                      </div>
                      <div className='fw-bold text-gray-800'>{kycRecord.contactNumber}</div>
                    </div>
                    <div className='col-md-4'>
                      <div className='text-gray-500 fs-7 mb-1'>
                        {intl.formatMessage({id: 'KYC.EMAIL', defaultMessage: 'Email'})}
                      </div>
                      <div className='fw-bold text-gray-800'>{kycRecord.emailAddress}</div>
                    </div>
                    <div className='col-md-4'>
                      <div className='text-gray-500 fs-7 mb-1'>
                        {intl.formatMessage({id: 'KYC.ID_TYPE', defaultMessage: 'ID Type'})}
                      </div>
                      <div className='fw-bold text-gray-800'>
                        {kycRecord.authorisedIdType
                          ? (ID_TYPE_LABELS[kycRecord.authorisedIdType] ?? kycRecord.authorisedIdType)
                          : '----'}
                      </div>
                    </div>
                    <div className='col-md-12'>
                      <div className='text-gray-500 fs-7 mb-1'>
                        {intl.formatMessage({id: 'KYC.ID_DOCUMENT', defaultMessage: 'ID Document'})}
                      </div>
                      {kycRecord.authorisedIdFile ? (
                        <div className='d-flex align-items-center justify-content-between border border-dashed border-gray-300 rounded px-6 py-4'>
                          <div className='d-flex align-items-center'>
                            <KTIcon iconName='file-down' className='fs-2 text-primary me-3' />
                            <span className='fw-bold text-gray-700'>
                              {intl.formatMessage({id: 'KYC.ID_DOCUMENT', defaultMessage: 'ID Document'})}
                            </span>
                          </div>
                          <div className='d-flex gap-2'>
                            <button
                              type='button'
                              className='btn btn-sm btn-primary d-flex align-items-center gap-1'
                              onClick={() => {
                                fetch(kycRecord.authorisedIdFile, {
                                  headers: {Authorization: `Bearer ${auth?.access_token}`},
                                })
                                  .then((res) => res.blob())
                                  .then((blob) => {
                                    const a = document.createElement('a')
                                    a.href = URL.createObjectURL(blob)
                                    a.download = 'authorised_id_document'
                                    a.click()
                                    URL.revokeObjectURL(a.href)
                                  })
                              }}
                            >
                              <KTIcon iconName='file-down' className='fs-3' />
                              {intl.formatMessage({id: 'KYC.DOWNLOAD', defaultMessage: 'Download'})}
                            </button>
                            <a
                              href={kycRecord.authorisedIdFile}
                              target='_blank'
                              rel='noreferrer'
                              className='btn btn-sm btn-secondary d-flex align-items-center gap-1'
                            >
                              <KTIcon iconName='eye' className='fs-3' />
                              {intl.formatMessage({id: 'KYC.VIEW_DOCUMENT', defaultMessage: 'View Document'})}
                            </a>
                          </div>
                        </div>
                      ) : (
                        <div className='fw-bold text-gray-800'>----</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* KYB Documents */}
                <div className='mb-8'>
                  <h5 className='fw-bold text-gray-700 mb-4 border-bottom pb-2'>
                    {intl.formatMessage({id: 'KYC.DOCUMENTS', defaultMessage: 'KYB Documents'})}
                  </h5>
                  {kycRecord.documents?.map((doc: any, index: number) => (
                    <div
                      key={index}
                      className='d-flex align-items-center justify-content-between border border-dashed border-gray-300 rounded px-6 py-4 mb-3'
                    >
                      <div className='d-flex align-items-center'>
                        <KTIcon iconName='file-down' className='fs-2 text-primary me-3' />
                        <span className='fw-bold text-gray-700'>{doc.name}</span>
                      </div>
                      <div className='d-flex gap-2'>
                        <button
                          type='button'
                          className='btn btn-sm btn-primary d-flex align-items-center gap-1'
                          onClick={() => {
                            fetch(
                              `${process.env.REACT_APP_API_URL}/kyb/documents/${doc.id}/download/`,
                              {headers: {Authorization: `Bearer ${auth?.access_token}`}}
                            )
                              .then((res) => res.blob())
                              .then((blob) => {
                                const a = document.createElement('a')
                                a.href = URL.createObjectURL(blob)
                                a.download = doc.fileName
                                a.click()
                                URL.revokeObjectURL(a.href)
                              })
                          }}
                        >
                          <KTIcon iconName='file-down' className='fs-3' />
                          {intl.formatMessage({id: 'KYC.DOWNLOAD', defaultMessage: 'Download'})}
                        </button>
                        <a
                          href={doc.url}
                          target='_blank'
                          rel='noreferrer'
                          className='btn btn-sm btn-secondary d-flex align-items-center gap-1'
                        >
                          <KTIcon iconName='eye' className='fs-3' />
                          {intl.formatMessage({id: 'KYC.VIEW_DOCUMENT', defaultMessage: 'View Document'})}
                        </a>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Additional Information */}
                <div className='mb-8'>
                  <h5 className='fw-bold text-gray-700 mb-4 border-bottom pb-2'>
                    {intl.formatMessage({id: 'KYC.ADDITIONAL_INFO', defaultMessage: 'Additional Information'})}
                  </h5>
                  <div className='row g-4'>
                    <div className='col-md-4'>
                      <div className='text-gray-500 fs-7 mb-1'>
                        {intl.formatMessage({id: 'KYC.SMS_TYPE', defaultMessage: 'SMS Type'})}
                      </div>
                      <div className='fw-bold text-gray-800'>
                        {kycRecord.smsType
                          ? (SMS_TYPE_LABELS[kycRecord.smsType] ?? kycRecord.smsType)
                          : '----'}
                      </div>
                    </div>
                    <div className='col-md-4'>
                      <div className='text-gray-500 fs-7 mb-1'>
                        {intl.formatMessage({id: 'KYC.SMS_VOLUME', defaultMessage: 'SMS Volume'})}
                      </div>
                      <div className='fw-bold text-gray-800'>
                        {kycRecord.smsPreferences?.[0]?.volume_min != null ||
                        kycRecord.smsPreferences?.[0]?.volume_max != null
                          ? `${(kycRecord.smsPreferences[0].volume_min ?? 0).toLocaleString()} – ${(kycRecord.smsPreferences[0].volume_max ?? 0).toLocaleString()}`
                          : '----'}
                      </div>
                    </div>
                    <div className='col-md-4'>
                      <div className='text-gray-500 fs-7 mb-1'>
                        {intl.formatMessage({id: 'KYC.FREQUENCY', defaultMessage: 'Frequency'})}
                      </div>
                      <div className='fw-bold text-gray-800'>
                        {kycRecord.smsPreferences?.[0]?.frequency
                          ? (FREQUENCY_LABELS[kycRecord.smsPreferences[0].frequency] ??
                            kycRecord.smsPreferences[0].frequency)
                          : '----'}
                      </div>
                    </div>
                    {kycRecord.description && (
                      <div className='col-md-12'>
                        <div className='text-gray-500 fs-7 mb-1'>
                          {intl.formatMessage({id: 'KYC.DESCRIPTION', defaultMessage: 'Description'})}
                        </div>
                        <div
                          className='border border-gray-300 rounded p-4 bg-light text-gray-700 fs-6'
                          dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(kycRecord.description)}}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className='text-gray-500 text-center py-10'>
                {intl.formatMessage({id: 'KYC.NO_RECORD', defaultMessage: 'No record selected'})}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export {DrawerKYCDetails}
